import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Button } from '@sparknz/spark-pattern-library';
import { trackCustomLink } from '@sparknz/tracking';

import { UPFRONT_PAYMENT_ID } from 'journey/productDetails/constants/ifp.constants';
import { useProductDetails } from 'journey/productDetails/provider';
import {
  IfpEligibilityCheckSteps,
  PlanBundleOptions,
} from 'journey/productDetails/typings';
import { trackLinkedProductsEvent } from 'journey/productDetails/utils';
import { getValidOfferIds } from 'journey/productDetails/utils/getValidOfferIds.utils';
import { useSegment } from 'shared/hooks';
import { TrackingCart } from 'shared/types/cart';
import { environmentVariables, extractFirstValue } from 'shared/utils';
import useAddToCart, {
  buildAddBundleInput,
} from '../../../../../../hooks/useAddToCart';

const AddToCartButton = () => {
  const {
    ifpEligibility,
    wearableEligibility,
    planBundleOption,
    selectedDevice,
    planOffers,
    paymentOptions,
  } = useProductDetails();

  const router = useRouter();
  const deviceId = extractFirstValue(router.query.offerId);
  const shouldExtractPlanId =
    planBundleOption === PlanBundleOptions.ChooseNewPlan ||
    ifpEligibility?.selectedLineNumber === PlanBundleOptions.ChooseNewPlan ||
    ifpEligibility?.isChangePlan;
  const planId = shouldExtractPlanId
    ? extractFirstValue(router.query.planId)
    : null;
  const ifpId = extractFirstValue(router.query.ifpId);

  const { addToCart, isAddToCartLoading } = useAddToCart('PRODUCT_DETAILS');
  const { channel, isBusiness, isLargeBusiness } = useSegment();

  if (isLargeBusiness) {
    return (
      <Link
        href={environmentVariables.mySparkDigitalSignInURL}
        legacyBehavior
        scroll={false}
        prefetch={false}
      >
        <Button variant="primary">Buy now</Button>
      </Link>
    );
  }

  const { stock } = selectedDevice;
  const isOutOfStock =
    stock?.warehouseStatus !== 'IN_STOCK' && stock?.storeStatus !== 'IN_STOCK';

  async function handleAddToCartClick() {
    trackCustomLink('Add to cart');
    const itemIds = getValidOfferIds(deviceId, planId, ifpId);

    const trackAddToCart = (cart: TrackingCart) => {
      trackLinkedProductsEvent({
        cart,
        device: selectedDevice,
        eventInfo: {
          eventName: 'Add to cart',
          eventAction: 'shop.addToCartAction',
        },
        plan: planOffers?.find(({ id }) => id === planId),
        paymentOption: paymentOptions?.find(({ id }) => id === ifpId),
        isBusiness,
        isChangePlan: ifpEligibility?.isChangePlan,
      });
    };

    const accountNumber =
      ifpEligibility.selectedAccountNumber ||
      wearableEligibility.selectedAccountNumber;

    const { Impersonate } = await import('@sparknz/online-web-shadow-mode');

    addToCart({
      input: {
        ...buildAddBundleInput({
          channel,
          itemIds,
          ifpEligibility,
          wearableEligibility,
        }),
        agentDetails: Impersonate.getAgentDetailsCookie(),
      },
      onAddedToCart: trackAddToCart,
      accountNumber,
    });
  }

  const isPayUpfront = Boolean(ifpId === UPFRONT_PAYMENT_ID);
  const isIneligibleForIfp = Boolean(
    ifpEligibility.activeStep === IfpEligibilityCheckSteps.DisableIfp,
  );

  const isInvalidPaymentOption =
    (isPayUpfront && !selectedDevice.soldSeparately) ||
    (!isPayUpfront && isIneligibleForIfp);

  const buttonText = selectedDevice.preOrderable ? 'Pre-order' : 'Add to cart';

  const isDisabled = isOutOfStock || isInvalidPaymentOption;

  return (
    <Button
      disabled={isDisabled}
      isLoading={isAddToCartLoading}
      variant="primary"
      onClick={handleAddToCartClick}
    >
      {buttonText}
    </Button>
  );
};

export default AddToCartButton;
